import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import useDayConfigurations from 'modules/work_schedules/hooks/useDayConfigurations'
import WorkSchedulePeriodForm from 'modules/work_schedules/lib/forms/WorkSchedulePeriodForm'
import overlapPeriods from 'modules/work_schedules/store/collections/overlapPeriods'
import type DayConfiguration from 'modules/work_schedules/store/models/DayConfiguration'
import type OverlapPeriod from 'modules/work_schedules/store/models/OverlapPeriod'
import type WorkSchedule from 'modules/work_schedules/store/models/Schedule'

import FixedPeriodForm from './FixedPeriodForm'
import FlexiblePeriodForm from './FlexiblePeriodForm'
import WarningModal from './WarningModal'

const AddOrEditPeriodModal = ({
  onClose,
  onSuccess,
  period,
  add,
  id,
  workSchedule,
  dayConfigurations = [],
  canAssignEmployees,
  canReassignAll,
  canAddBreaks,
}: {
  onClose: () => void
  onSuccess: () => void
  period: OverlapPeriod | undefined
  add?: boolean
  id?: string
  workSchedule?: WorkSchedule
  dayConfigurations?: DayConfiguration[]
  canAssignEmployees: boolean
  canReassignAll: boolean
  canAddBreaks: boolean
}) => {
  const [openWarning, setOpenWarning] = React.useState(false)

  const periodFormRef = useLazyRef(() => new WorkSchedulePeriodForm(period?.toJS() ?? {})).current

  const {
    dayConfigForms,
    totalHours,
    onAddDayConfig,
    onDeleteDayConfig,
    onCopyFirstToAll,
    onAddBreak,
    onDeleteBreak,
    saveDayConfigurations,
  } = useDayConfigurations(dayConfigurations, periodFormRef.get('schedule_type').value)

  const getStartAndEndDates = () => {
    const [, startMonth, startDay] = stringToDate(periodFormRef.get('start_date').value, 'ui')
      .format('YYYY/MM/DD')
      .split('/')

    const [, endMonth, endDay] = stringToDate(periodFormRef.get('end_date').value, 'ui')
      .format('YYYY/MM/DD')
      .split('/')

    return {
      start_day: startDay,
      start_month: startMonth,
      end_day: endDay,
      end_month: endMonth,
    }
  }

  const modifiedPeriod = {
    ...getStartAndEndDates(),
  }

  const savePeriod = async () => {
    const payload = {
      work_schedule_schedule_id: id,
      schedule_type: periodFormRef.get('schedule_type').value,
      ...modifiedPeriod,
    }

    return add ? await overlapPeriods.create(payload) : await period?.save(modifiedPeriod)
  }

  const { running, performAction } = useAsyncAction(async () => {
    const response = await savePeriod()

    if (response.id) {
      await saveDayConfigurations(response.id)
    }

    setOpenWarning(false)
    onClose()
    onSuccess()
  })

  const noOfEmployees = !workSchedule || add ? 0 : workSchedule.employeeIds.length

  const canReassignAllEmployees = workSchedule ? canReassignAll : false

  const canEdit = !noOfEmployees || canReassignAllEmployees
  const onConfirm = () => {
    if (!canEdit) {
      return
    }

    performAction()
  }

  const submitText = add
    ? i18n.t('settings.work_schedule.period_section.add_period_modal.add')
    : i18n.t('settings.work_schedule.period_section.edit_period_modal.add')

  const disabled = !canEdit || running

  const onSubmit = () => {
    noOfEmployees > 0 && !add && !openWarning ? setOpenWarning(true) : performAction()
  }

  return (
    <Box padding="s32" background="grey200" gap="s32" borderRadius={{ all: 'abs008' }}>
      {periodFormRef.isFixed ? (
        <FixedPeriodForm
          edit
          onSubmit={onSubmit}
          periodFormRef={periodFormRef}
          dayConfigForms={dayConfigForms}
          totalHours={totalHours}
          onAddDayConfig={onAddDayConfig}
          onDeleteDayConfig={onDeleteDayConfig}
          onCopyFirstToAll={onCopyFirstToAll}
          onAddBreak={onAddBreak}
          onDeleteBreak={onDeleteBreak}
          submitText={submitText}
          canAddBreaks={canAddBreaks}
        />
      ) : (
        <FlexiblePeriodForm
          edit
          onSubmit={onSubmit}
          periodFormRef={periodFormRef}
          dayConfigForms={dayConfigForms}
          totalHours={totalHours}
          onCopyFirstToAll={onCopyFirstToAll}
          onAddBreak={onAddBreak}
          onDeleteBreak={onDeleteBreak}
          submitText={submitText}
          canAddBreaks={canAddBreaks}
        />
      )}
      {openWarning && workSchedule && (
        <WarningModal
          onSubmit={onSubmit}
          onConfirm={onConfirm}
          onClose={onClose}
          workSchedule={workSchedule}
          disabled={disabled}
          isPeriod
          canReassignAll={canReassignAllEmployees}
          canAssignEmployees={canAssignEmployees}
        />
      )}
    </Box>
  )
}

export default observer(AddOrEditPeriodModal)
